import React from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import { IPhoneMockup } from "react-device-mockup";
import { CopyIcon, ExternalLinkIcon, ImageIcon, MobileIcon } from "@radix-ui/react-icons";
import { getWhatsappButtonTypeConfig, getWhatsappHeaderTypeLabel } from "./marketingWhatsappStore";

const BUTTON_ICON = {
  website: ExternalLinkIcon,
  phone: MobileIcon,
  copy_code: CopyIcon,
};

function WhatsappHeaderMedia({ headerType, attachment, label, compact }) {
  const height = compact ? 74 : 150;

  if (headerType === "image" && attachment?.url) {
    return <img src={attachment.url} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
  }

  if (headerType === "video" && attachment?.url) {
    return <video src={attachment.url} muted controls={!compact} style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
  }

  if (headerType === "document" && attachment?.url && (attachment.type === "application/pdf" || attachment.name?.toLowerCase().endsWith(".pdf"))) {
    return (
      <object title={label} data={attachment.url} type="application/pdf" style={{ width: "100%", height, border: 0, background: "#fff" }}>
        <iframe title={label} src={attachment.url} style={{ width: "100%", height, border: 0, background: "#fff" }} />
      </object>
    );
  }

  return (
    <>
      <ImageIcon width={compact ? "18" : "28"} height={compact ? "18" : "28"} style={{ color: "#6b7280" }} />
      <Text size="1" color="gray">
        {headerType === "location" ? "Location placeholder header" : label}
      </Text>
    </>
  );
}

export default function MarketingWhatsappPreview({ template, compact = false }) {
  const hasButtons = template.buttons.length > 0;
  const headerType = template.headerType || (template.mediaEnabled ? template.mediaType || "image" : template.headerEnabled ? "text" : "none");
  const hasMediaHeader = !["none", "text"].includes(headerType);
  const headerAttachment = template.headerAttachment;
  const mediaHeaderLabel = headerType === "location"
    ? "Location placeholder header"
    : template.headerMediaLabel || template.mediaLabel || `${getWhatsappHeaderTypeLabel(headerType)} header`;
  const screenWidth = compact ? 210 : 300;
  const deviceHeight = compact ? 344 : 500;
  const mockupScale = compact ? 0.58 : 0.95;
  const wrapperHeight = compact ? 186 : "100%";
  const screenPadding = compact ? "30px 12px 0" : "30px 16px 0";
  const bubbleFontSize = compact ? 10.5 : 13.5;
  const headerFontSize = compact ? 11.5 : 15.5;
  const footerFontSize = compact ? 10 : 11.5;
  const timeFontSize = compact ? 10 : 11.5;
  const buttonFontSize = compact ? 11.5 : 14.5;
  const bubble = (
    <Box
      style={{
        width: compact ? "100%" : 250,
        maxWidth: compact ? "100%" : "86%",
        borderRadius: compact ? 10 : 12,
        background: "#fff",
        border: "1px solid rgba(55, 100, 164, 0.12)",
        boxShadow: "0 2px 4px rgba(15, 23, 42, 0.12)",
        overflow: "hidden",
        alignSelf: "flex-start",
        position: "relative",
      }}
    >
      {hasMediaHeader ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="2"
          style={{
            height: compact ? 74 : 150,
            background: "#f3f4f6",
            borderBottom: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <WhatsappHeaderMedia headerType={headerType} attachment={headerAttachment} label={mediaHeaderLabel} compact={compact} />
        </Flex>
      ) : null}

      {headerType === "text" && template.headerText ? (
        <Box px="3" pt="3" pb="1">
          <Text as="div" style={{ color: "#111827", fontWeight: 600, lineHeight: 1.25, fontSize: headerFontSize }}>
            {template.headerText}
          </Text>
        </Box>
      ) : null}

      <Box px="3" pt={headerType === "text" && template.headerText ? "1" : "3"} pb="2">
        <Text
          as="div"
          style={{
            color: "#111827",
            whiteSpace: "pre-wrap",
            fontWeight: 400,
            lineHeight: 1.38,
            fontSize: bubbleFontSize,
            display: compact ? "-webkit-box" : "block",
            WebkitLineClamp: compact ? 4 : "initial",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {template.body || "Your message body appears here."}
        </Text>

        {(template.footerEnabled && template.footerText) || !compact ? (
          <Flex align="end" justify="between" gap="2" mt="2">
            {template.footerEnabled && template.footerText ? (
              <Text as="div" style={{ color: "#8b949e", fontWeight: 400, lineHeight: 1.25, fontSize: footerFontSize, minWidth: 0 }}>
                {template.footerText}
              </Text>
            ) : (
              <Box />
            )}
            {!compact ? (
              <Text as="div" style={{ color: "#8b949e", fontWeight: 400, fontSize: timeFontSize, lineHeight: 1.1, flexShrink: 0 }}>
                10:06 PM
              </Text>
            ) : null}
          </Flex>
        ) : null}
      </Box>

      {hasButtons ? (
        <Flex direction="column" style={{ borderTop: "1px solid #d1d5db" }}>
          {template.buttons.slice(0, compact ? 1 : template.buttons.length).map((button) => {
            const ButtonIcon = BUTTON_ICON[button.type] || ExternalLinkIcon;
            return (
              <Flex
                key={button.id}
                align="center"
                justify="center"
                gap="2"
                style={{
                  minHeight: compact ? 34 : 42,
                  borderTop: "1px solid #e5e7eb",
                  color: "#16825d",
                }}
              >
                <ButtonIcon width={compact ? "14" : "16"} height={compact ? "14" : "16"} />
                <Text style={{ color: "#16825d", fontSize: buttonFontSize, fontWeight: 400 }}>
                  {button.label || getWhatsappButtonTypeConfig(button.type).label}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      ) : null}
    </Box>
  );

  if (compact) {
    return (
      <Flex
        align="start"
        justify="center"
        p="3"
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          backgroundColor: "#eee8df",
          backgroundImage:
            "radial-gradient(circle at 22px 18px, rgba(104, 93, 82, 0.08) 0 1px, transparent 2px), radial-gradient(circle at 62px 58px, rgba(104, 93, 82, 0.07) 0 2px, transparent 3px), linear-gradient(135deg, rgba(104, 93, 82, 0.035) 25%, transparent 25%, transparent 50%, rgba(104, 93, 82, 0.035) 50%, rgba(104, 93, 82, 0.035) 75%, transparent 75%, transparent)",
          backgroundSize: "44px 44px, 72px 72px, 36px 36px",
        }}
      >
        {bubble}
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="center"
      style={{
        width: "100%",
        height: "100%",
        minHeight: compact ? 148 : 0,
        overflow: compact ? "hidden" : "auto",
        background: compact ? "#e9eef4" : "transparent",
      }}
    >
      <Box
        style={{
          height: wrapperHeight,
          width: "100%",
          display: "flex",
          alignItems: compact ? "center" : "flex-start",
          justifyContent: "center",
          overflow: compact ? "hidden" : "auto",
          paddingTop: compact ? 4 : 12,
        }}
      >
        <Box
          style={{
            transform: `scale(${mockupScale})`,
            transformOrigin: "top center",
            flexShrink: 0,
          }}
        >
          <IPhoneMockup
            screenWidth={screenWidth}
            frameColor="var(--gray-12)"
            statusbarColor="var(--gray-contrast)"
            transparentNavBar="true"
            hideStatusBar="true"
            screenType="notch"
          >
            <Flex
              direction="column"
              justify="start"
              style={{
                width: "100%",
                minHeight: deviceHeight,
                padding: screenPadding,
                backgroundColor: "#eee8df",
                backgroundImage:
                  "radial-gradient(circle at 22px 18px, rgba(104, 93, 82, 0.08) 0 1px, transparent 2px), radial-gradient(circle at 62px 58px, rgba(104, 93, 82, 0.07) 0 2px, transparent 3px), linear-gradient(135deg, rgba(104, 93, 82, 0.035) 25%, transparent 25%, transparent 50%, rgba(104, 93, 82, 0.035) 50%, rgba(104, 93, 82, 0.035) 75%, transparent 75%, transparent)",
                backgroundSize: "44px 44px, 72px 72px, 36px 36px",
              }}
            >
              {bubble}
            </Flex>
          </IPhoneMockup>
        </Box>
      </Box>
    </Flex>
  );
}
